import { Injectable, OnDestroy } from '@angular/core';
import { Subject, Observable } from 'rxjs';
import { API } from '../../constants/api-endpoints';
import { OperacaoSessaoService } from './operacao-sessao.service';
import { SessaoOperacao } from './operacao-sessao.model';

// Motivos pelos quais uma sessao ativa foi encerrada. Emitido no
// Observable sessaoEncerrada$ para que qualquer componente ouvindo
// (ex: paginas de operacao) reaja apropriadamente.
export type MotivoEncerramento = 'manual' | 'perdida' | 'logout';

// Gerencia o CICLO DE VIDA de uma sessao ativa no cliente:
// - Guarda a sessao corrente em memoria (uma so por sessao de navegador)
// - Dispara heartbeat a cada 60s
// - Se o heartbeat receber 404, a sessao foi perdida -> emite evento
// - Ao fechar o browser (beforeunload), dispara sendBeacon para encerrar
//
// NAO faz navegacao nem toast — quem consumir o sessaoEncerrada$ decide.
@Injectable({
  providedIn: 'root'
})
export class SessaoHeartbeatService implements OnDestroy {
  private static readonly IntervaloHeartbeatMs = 60_000;

  private sessaoAtual: SessaoOperacao | null = null;
  private timerHandle: ReturnType<typeof setInterval> | null = null;

  private readonly sessaoEncerradaSubject = new Subject<MotivoEncerramento>();
  readonly sessaoEncerrada$: Observable<MotivoEncerramento> =
    this.sessaoEncerradaSubject.asObservable();

  private readonly onBeforeUnload = () => this.encerrarViaBeacon();

  constructor(private api: OperacaoSessaoService) {
    window.addEventListener('beforeunload', this.onBeforeUnload);
  }

  // Consultado por componentes que querem saber se ha sessao ativa
  // (ex: guard que so libera as abas de operacao com sessao aberta).
  get sessao(): SessaoOperacao | null {
    return this.sessaoAtual;
  }

  // Registra a sessao aberta e liga o heartbeat. Se ja havia uma outra
  // sessao ativa, a anterior e substituida (mas nao encerrada no backend —
  // quem chamou iniciar() e responsavel por isso, ja que o backend
  // encerra a antiga automaticamente do lado dele).
  iniciar(sessao: SessaoOperacao): void {
    this.sessaoAtual = sessao;
    this.pararTimer();

    this.timerHandle = setInterval(
      () => this.dispararHeartbeat(),
      SessaoHeartbeatService.IntervaloHeartbeatMs
    );
  }

  // Encerra manualmente a sessao (ex: usuario voltou para a tela de
  // selecao de empreendimento). Chama o backend E limpa o estado local.
  encerrar(): void {
    const sessao = this.sessaoAtual;
    if (!sessao) return;

    this.pararTimer();
    this.sessaoAtual = null;

    this.api.encerrar(sessao.identificadorSessao).subscribe({
      next: () => this.sessaoEncerradaSubject.next('manual'),
      error: () => this.sessaoEncerradaSubject.next('manual')
    });
  }

  // Usado no AuthService.logout() ANTES do msalService.logoutRedirect().
  // Ja limpa o estado local — o AuthService nao precisa esperar
  // resposta HTTP para continuar o fluxo do logout.
  encerrarTodasDoUsuario(): Promise<void> {
    this.pararTimer();
    this.sessaoAtual = null;

    return new Promise((resolve) => {
      this.api.encerrarTodasDoUsuario().subscribe({
        next: () => {
          this.sessaoEncerradaSubject.next('logout');
          resolve();
        },
        error: () => {
          // Nao bloqueia o logout mesmo se a chamada falhar — o lazy
          // cleanup do backend limpa a sessao em ate 5min.
          this.sessaoEncerradaSubject.next('logout');
          resolve();
        }
      });
    });
  }

  ngOnDestroy(): void {
    window.removeEventListener('beforeunload', this.onBeforeUnload);
    this.pararTimer();
  }

  // ---------------- privados ----------------

  private dispararHeartbeat(): void {
    const sessao = this.sessaoAtual;
    if (!sessao) return;

    this.api.heartbeat(sessao.identificadorSessao).subscribe({
      error: (err) => {
        // 404 = sessao ja nao existe no backend (foi encerrada por
        // outro fluxo, forcada por admin ou expirou). Considera perdida
        // e avisa quem estiver ouvindo, para que o usuario seja mandado
        // de volta.
        if (err?.status === 404) {
          this.pararTimer();
          this.sessaoAtual = null;
          this.sessaoEncerradaSubject.next('perdida');
        }
        // Outros erros (500, rede) — silencio. O proximo heartbeat
        // tenta de novo em 60s; se o backend continuar down, a sessao
        // expira sozinha por inatividade e o cliente cai no 404.
      }
    });
  }

  // Encerramento sincrono via sendBeacon: garante que o encerramento saia
  // do browser mesmo com a aba fechando. Usa endpoint dedicado
  // (encerrar-beacon) porque sendBeacon so aceita POST, nao DELETE, e
  // porque queremos distinguir em auditoria "fechou o browser" de
  // "clicou em sair".
  private encerrarViaBeacon(): void {
    const sessao = this.sessaoAtual;
    if (!sessao) return;

    try {
      navigator.sendBeacon(
        API.operacoesSessao.encerrarBeacon(sessao.identificadorSessao)
      );
    } catch {
      // Sem-op — o cliente ja esta fechando; se falhar, o lazy cleanup
      // limpa a sessao em ate 5min por inatividade.
    }
  }

  private pararTimer(): void {
    if (this.timerHandle !== null) {
      clearInterval(this.timerHandle);
      this.timerHandle = null;
    }
  }
}
